import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import {
  eliminarFamiliar,
  listarObservacionesV2,
  resolverObservacion,
  type LegajoConObservacionesV2,
  type ObservacionItem,
  type ObservacionLegajoV2,
  type ResolverObservacionBody,
} from '@lib/services/observaciones.service';
import { CAMPO_OPCIONES } from '@lib/catalogs';
import { useCanMutate } from '@lib/hooks/useCanMutate';
import { ResolverObservacionModal } from '@components/ResolverObservacionModal';
import { EliminarFamiliarModal } from '@components/EliminarFamiliarModal';
import { TableSkeleton } from '@components/medicus-ui/Loaders';

const PAGE_SIZE = 20;

const CAMPO_LABEL: Record<string, string> = {
  documento: 'Documento',
  cuil: 'CUIL',
  fechaNacimiento: 'Fecha de nacimiento',
  nombres: 'Nombres',
  apellido: 'Apellido',
  apellSolt: 'Apellido',
  sexo: 'Sexo',
  estadoCivil: 'Estado civil',
  nacionalidad: 'Nacionalidad',
  tipoDoc: 'Tipo de documento',
  parentesco: 'Parentesco',
};

const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

interface Filtro {
  campo?: string;
  valorPropuesto?: string;
}

export function ObservacionesPage() {
  const puedeMutar = useCanMutate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filtro, setFiltro] = useState<Filtro>({});
  const [busquedaInput, setBusquedaInput] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [resolverSel, setResolverSel] = useState<ObservacionItem | null>(null);
  const [eliminarSel, setEliminarSel] = useState<ObservacionItem | null>(null);

  // Búsqueda (legajo / DNI / nombre del titular) con debounce: aplica 300ms
  // después de tipear y vuelve a la página 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(busquedaInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaInput]);

  const q = useQuery({
    queryKey: ['observaciones-v2', page, filtro.campo ?? '', filtro.valorPropuesto ?? '', busqueda],
    queryFn: () =>
      listarObservacionesV2({
        page,
        pageSize: PAGE_SIZE,
        campo: filtro.campo,
        valorPropuesto: filtro.valorPropuesto,
        q: busqueda || undefined,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const resolverMutation = useMutation({
    mutationFn: (args: {
      legajoId: string;
      obsIndex: number;
      body: ResolverObservacionBody;
    }) => resolverObservacion(args.legajoId, args.obsIndex, args.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['observaciones-v2'] });
      setResolverSel(null);
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (args: { legajoId: string; asociadoId: string; confirmDocumento: string }) =>
      eliminarFamiliar(args.legajoId, args.asociadoId, args.confirmDocumento),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['observaciones-v2'] });
      setEliminarSel(null);
    },
  });

  const totalPages = Math.max(1, Math.ceil((q.data?.total ?? 0) / PAGE_SIZE));

  function aplicarFiltro(nuevo: Filtro) {
    setFiltro(nuevo);
    setPage(1);
  }

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Observaciones pendientes</h1>
        <p className="text-element-mid">
          Los titulares reportaron datos que consideran incorrectos. Al{' '}
          <strong>Resolver</strong> podés editar <strong>todos los campos</strong> del integrante
          (el campo reportado viene pre-cargado con el valor propuesto) o{' '}
          <strong>rechazar</strong> sin cambios. Si la observación es sobre un cónyuge, hijo o
          adherente, también podés eliminar al integrante del legajo.
        </p>
      </header>

      <div style={{ marginTop: 16, maxWidth: 360 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 10px',
            border: '1px solid var(--color-outline, #E2E8F0)',
            borderRadius: 'var(--radius-md, 8px)',
            background: 'var(--color-background, #fff)',
          }}
        >
          <Search size={15} strokeWidth={2} style={{ opacity: 0.55, flexShrink: 0 }} />
          <input
            type="text"
            value={busquedaInput}
            onChange={(e) => setBusquedaInput(e.target.value)}
            placeholder="Buscar por legajo, DNI o nombre del titular"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              padding: '8px 0',
              background: 'transparent',
              fontSize: 13,
            }}
          />
          {busquedaInput && (
            <button
              type="button"
              onClick={() => setBusquedaInput('')}
              title="Limpiar"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 }}
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {q.data && q.data.agrupaciones.length > 0 && (
        <Agrupaciones
          agrupaciones={q.data.agrupaciones}
          filtroActual={filtro}
          onFiltrar={aplicarFiltro}
        />
      )}

      <div className="rowBetween" style={{ alignItems: 'baseline', marginTop: 20 }}>
        <span className="text-element-mid" style={{ fontSize: 13 }}>
          {(q.data?.total ?? 0).toLocaleString('es-AR')} legajos con observaciones
          {filtro.campo && (
            <>
              {' '}— filtrado por <code>{CAMPO_LABEL[filtro.campo] ?? filtro.campo}</code>
              {filtro.valorPropuesto !== undefined && (
                <> = "<strong>{filtro.valorPropuesto || '(vacío)'}</strong>"</>
              )}{' '}
              <button
                type="button"
                className="btn btn-link"
                style={{ fontSize: 12 }}
                onClick={() => aplicarFiltro({})}
              >
                limpiar
              </button>
            </>
          )}
        </span>
        {q.data && q.data.total > PAGE_SIZE && (
          <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
            Página {page} de {totalPages}
          </span>
        )}
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {q.isLoading && <TableSkeleton rows={PAGE_SIZE} />}
        {q.isError && <p className="status status-error">No se pudo cargar.</p>}
        {q.data && q.data.items.length === 0 && (
          <p className="text-element-mid">No hay observaciones pendientes con este filtro.</p>
        )}
        {q.data?.items.map((legajo) => (
          <LegajoCard
            key={legajo.legajoId}
            legajo={legajo}
            puedeMutar={puedeMutar}
            onResolver={(obs) => setResolverSel(toObservacionItem(legajo, obs))}
            onEliminar={(obs) => setEliminarSel(toObservacionItem(legajo, obs))}
          />
        ))}
      </div>

      {q.data && q.data.total > PAGE_SIZE && (
        <footer
          className="rowBetween"
          style={{
            alignItems: 'center',
            marginTop: 20,
            paddingTop: 12,
            borderTop: '1px solid var(--color-border, #E2E8F0)',
          }}
        >
          <span className="text-element-mid" style={{ fontSize: 12 }}>
            Mostrando {(page - 1) * PAGE_SIZE + 1}-
            {Math.min(page * PAGE_SIZE, q.data.total)} de {q.data.total}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente
            </button>
          </div>
        </footer>
      )}

      {resolverSel && (
        <ResolverObservacionModal
          item={resolverSel}
          submitting={resolverMutation.isPending}
          error={resolverMutation.error ? errorMessage(resolverMutation.error) : null}
          onClose={() => {
            setResolverSel(null);
            resolverMutation.reset();
          }}
          onSubmit={(body) =>
            resolverMutation.mutate({
              legajoId: resolverSel.legajoId,
              obsIndex: resolverSel.obsIndex,
              body,
            })
          }
        />
      )}

      {eliminarSel && (
        <EliminarFamiliarModal
          item={eliminarSel}
          submitting={eliminarMutation.isPending}
          error={eliminarMutation.error ? errorMessage(eliminarMutation.error) : null}
          onClose={() => {
            setEliminarSel(null);
            eliminarMutation.reset();
          }}
          onConfirm={(confirmDocumento) =>
            eliminarMutation.mutate({
              legajoId: eliminarSel.legajoId,
              asociadoId: eliminarSel.asociadoId,
              confirmDocumento,
            })
          }
        />
      )}
    </section>
  );
}

function Agrupaciones(props: {
  agrupaciones: Array<{ campo: string; valorPropuesto: string; cantidad: number }>;
  filtroActual: Filtro;
  onFiltrar: (f: Filtro) => void;
}) {
  const { agrupaciones, filtroActual, onFiltrar } = props;
  const [abierto, setAbierto] = useState(false);
  const hayFiltro = filtroActual.campo !== undefined;
  return (
    <div style={{ marginTop: 14 }}>
      {/* Toggle sutil: por defecto colapsado para no ocupar espacio. */}
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
          color: 'var(--color-element-mid)',
          fontSize: 12,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ fontSize: 10 }}>{abierto ? '▾' : '▸'}</span>
        Agrupar por valor propuesto ({agrupaciones.length})
        {hayFiltro && !abierto && (
          <span style={{ color: 'var(--color-primary-darker)', fontWeight: 600 }}>· filtro activo</span>
        )}
      </button>
      {abierto && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {agrupaciones.map((a) => {
            const activo =
              filtroActual.campo === a.campo &&
              (filtroActual.valorPropuesto ?? '') === a.valorPropuesto;
            return (
              <button
                key={`${a.campo}-${a.valorPropuesto}`}
                type="button"
                onClick={() =>
                  onFiltrar(activo ? {} : { campo: a.campo, valorPropuesto: a.valorPropuesto })
                }
                style={{
                  padding: '3px 8px',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: activo ? 'var(--color-primary-darker)' : 'var(--color-border, #E2E8F0)',
                  background: activo ? 'var(--color-medicus-celeste-bg-soft, #DBEAFE)' : 'white',
                  fontSize: 11,
                  cursor: 'pointer',
                  color: 'inherit',
                  font: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <span style={{ fontWeight: 600 }}>{CAMPO_LABEL[a.campo] ?? a.campo}</span>
                <span style={{ color: 'var(--color-element-mid)' }}>
                  = "{a.valorPropuesto || '(vacío)'}"
                </span>
                <span
                  style={{
                    background: activo ? 'var(--color-primary-darker)' : '#F1F5F9',
                    color: activo ? 'white' : 'inherit',
                    padding: '0 6px',
                    borderRadius: 999,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                  }}
                >
                  {a.cantidad.toLocaleString('es-AR')}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LegajoCard(props: {
  legajo: LegajoConObservacionesV2;
  puedeMutar: boolean;
  onResolver: (obs: ObservacionLegajoV2) => void;
  onEliminar: (obs: ObservacionLegajoV2) => void;
}) {
  const { legajo, puedeMutar, onResolver, onEliminar } = props;
  const titularLabel = `${legajo.titularApellido} ${legajo.titularNombres}`.trim() || '—';
  return (
    <article className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header className="rowBetween" style={{ alignItems: 'baseline', gap: 12 }}>
        <div>
          <h3 className="heading-5" style={{ margin: 0 }}>
            Legajo {legajo.legajoNumero}
            <span style={{ marginLeft: 8, color: 'var(--color-element-mid)', fontWeight: 400, fontSize: 13 }}>
              · {titularLabel} {legajo.titularDocumento && `· DNI ${legajo.titularDocumento}`}
            </span>
          </h3>
        </div>
        <span
          className="status status-warning"
          style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          {legajo.observaciones.length} obs pendiente
          {legajo.observaciones.length === 1 ? '' : 's'}
        </span>
      </header>

      <section>
        <p className="text-element-mid" style={{ fontSize: 11, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Integrantes
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
          {legajo.integrantes.map((i) => (
            <li
              key={i.asociadoId}
              style={{
                padding: '4px 0',
                paddingLeft: i.esTitular ? 0 : 16,
                borderLeft: i.esTitular ? 'none' : '2px solid var(--color-border, #E2E8F0)',
                marginLeft: i.esTitular ? 0 : 8,
                display: 'flex',
                gap: 10,
                alignItems: 'baseline',
              }}
            >
              <span style={{ fontWeight: i.esTitular ? 600 : 400 }}>
                {i.apellSolt} {i.nombres}
              </span>
              <span style={{ color: 'var(--color-element-mid)', fontSize: 12 }}>
                DNI {i.documento || '—'}
              </span>
              <span style={{ color: 'var(--color-element-mid)', fontSize: 12 }}>
                {i.esTitular ? 'Titular' : PARENTESCO_LABEL[i.parentesco] ?? `Parent. ${i.parentesco}`}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <p className="text-element-mid" style={{ fontSize: 11, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Observaciones
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {legajo.observaciones.map((o) => (
            <ObservacionRow
              key={`${legajo.legajoId}-${o.obsIndex}`}
              obs={o}
              puedeMutar={puedeMutar}
              onResolver={() => onResolver(o)}
              onEliminar={() => onEliminar(o)}
            />
          ))}
        </div>
      </section>
    </article>
  );
}

function ObservacionRow(props: {
  obs: ObservacionLegajoV2;
  puedeMutar: boolean;
  onResolver: () => void;
  onEliminar: () => void;
}) {
  const { obs, puedeMutar, onResolver, onEliminar } = props;
  const esTitular = obs.asociadoParentesco === '0';
  return (
    <div
      style={{
        padding: '10px 12px',
        background: 'var(--color-surface-alt, #F8FAFC)',
        border: '1px solid var(--color-border, #E2E8F0)',
        borderRadius: 6,
        display: 'grid',
        gridTemplateColumns: 'minmax(200px, 1.5fr) minmax(120px, 1fr) minmax(120px, 1fr) auto',
        gap: 10,
        alignItems: 'baseline',
      }}
    >
      <div style={{ fontSize: 13 }}>
        <div style={{ fontWeight: 600 }}>
          {CAMPO_LABEL[obs.campo] ?? obs.campo}
          <span style={{ marginLeft: 8, color: 'var(--color-element-mid)', fontWeight: 400, fontSize: 12 }}>
            sobre {obs.asociadoApellido} {obs.asociadoNombres}
          </span>
        </div>
        {obs.mensaje && (
          <div style={{ marginTop: 4, fontSize: 12, fontStyle: 'italic', color: 'var(--color-element-mid)' }}>
            “{obs.mensaje}”
          </div>
        )}
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--color-element-mid)' }}>
          {new Date(obs.reportadoAt).toLocaleString('es-AR')}
        </div>
      </div>
      <div style={{ fontSize: 12 }}>
        <p style={{ margin: 0, color: 'var(--color-element-mid)', fontSize: 11 }}>Valor actual</p>
        <code style={{ fontSize: 12 }}>{obs.valorActual || '—'}</code>
      </div>
      <div style={{ fontSize: 12 }}>
        <p style={{ margin: 0, color: 'var(--color-element-mid)', fontSize: 11 }}>Valor propuesto</p>
        <code style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>
          {obs.valorPropuesto || '(sin valor)'}
        </code>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
        <button
          type="button"
          className="btn btn-primary btn--sm"
          disabled={!puedeMutar}
          onClick={onResolver}
        >
          Resolver
        </button>
        {!esTitular && (
          <button
            type="button"
            className="btn btn-ghost btn--sm"
            style={{ color: 'var(--color-error-dark)' }}
            disabled={!puedeMutar}
            onClick={onEliminar}
          >
            Eliminar integrante
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Adapta el shape v2 al `ObservacionItem` que esperan los modales viejos
 * (ResolverObservacionModal / EliminarFamiliarModal). Mantengo los modales
 * intactos para no tocar lógica probada — solo mapeo los campos.
 */
function toObservacionItem(
  legajo: LegajoConObservacionesV2,
  obs: ObservacionLegajoV2,
): ObservacionItem {
  const esTitular = obs.asociadoParentesco === '0';
  const esEnum = ['sexo', 'nacionalidad', 'tipoDoc', 'parentesco', 'estadoCivil'].includes(obs.campo);
  return {
    legajoId: legajo.legajoId,
    legajoNumero: legajo.legajoNumero,
    padronId: legajo.padronId,
    empresaId: legajo.empresaId,
    obsIndex: obs.obsIndex,
    campo: obs.campo,
    esEnum,
    valorActual: obs.valorActual ?? '',
    valorPropuesto: obs.valorPropuesto ?? '',
    mensaje: obs.mensaje ?? '',
    reportadoAt: obs.reportadoAt,
    asociadoId: obs.asociadoId,
    asociadoNombre: `${obs.asociadoApellido} ${obs.asociadoNombres}`.trim(),
    asociadoDocumento: '',
    asociadoParentesco: obs.asociadoParentesco,
    esTitular,
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err && 'response' in err) {
    const r = (err as { response?: { data?: { message?: string } } }).response;
    if (r?.data?.message) return r.data.message;
  }
  return 'Error desconocido';
}

export { CAMPO_OPCIONES };
